/**
 * Drop info tooltip content with Copy for Apply buttons.
 * Two options: config only (campaign, gateway, senders) or config + texts.
 * For Media Devoted | Created By Ciro Autiero
 */

import { Copy, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useToast } from '@/providers/ToastProvider';
import { useDropApplyStore } from '@/stores/drop-apply.store';
import { filterSenderNamesMax11 } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';

import { StatsTooltipSection } from '../../StatsHoverPopover';
import { orDash } from '../../shared';

const DEFAULT_TAG_NAME = 'SMS';

function formatSenderId(senderName: string[] | null | undefined): string {
  if (!senderName?.length) return '—';
  return senderName.join(', ');
}

function toSenderNames(drop: DropsWithStatsRow['drop']): string[] {
  const raw = Array.isArray(drop.senderName)
    ? drop.senderName.filter((s): s is string => typeof s === 'string')
    : drop.senderName ? [String(drop.senderName)] : [];
  return filterSenderNamesMax11(raw);
}

function toSmsTexts(drop: DropsWithStatsRow['drop']): string[] {
  if (!drop.smsText?.length) return [];
  return drop.smsText.filter((t): t is string => typeof t === 'string');
}

export function DropInfoTooltipContent({
  drop,
  dropSequence,
  countryKey,
}: {
  drop: DropsWithStatsRow['drop'];
  dropSequence?: number;
  /** Country slug from the route — enables the "Open in Tool (SMSWiser)" deep-link. */
  countryKey?: string;
}) {
  const setValues = useDropApplyStore((s) => s.setValues);
  const { success: toastSuccess } = useToast();
  const navigate = useNavigate();
  const gw = drop.smsGatewayRoute?.trim() || '—';
  const sid = formatSenderId(drop.senderName);
  const title = drop.title || '—';
  const leads = orDash(drop.leadsList);
  const gatewayName = gw === '—' || !drop.smsGatewayRoute?.trim() ? null : gw;

  const handleCopyConfig = () => {
    setValues({
      campaignName: drop.campaign?.trim() || null,
      gatewayName,
      senderNames: toSenderNames(drop),
    });
    toastSuccess(
      'Config copied',
      'Campaign, gateway, and senders. Use in Scale modal, Apply on drafts, or Apply copied on sent/scheduled (opens editor).',
    );
  };

  const handleCopyConfigWithTexts = () => {
    const texts = toSmsTexts(drop);
    setValues({
      campaignName: drop.campaign?.trim() || null,
      gatewayName,
      senderNames: toSenderNames(drop),
      smsTexts: texts.length > 0 ? texts : undefined,
    });
    toastSuccess(
      'Config + texts copied',
      texts.length > 0
        ? 'Campaign, gateway, senders, and texts. Use in Scale, Apply on drafts, or Apply copied on sent/scheduled.'
        : 'Campaign, gateway, and senders (no texts). Use in Scale, Apply on drafts, or Apply copied on sent/scheduled.',
    );
  };

  const canOpenInTool = Boolean(countryKey && drop.uniqueId);
  const handleOpenInTool = () => {
    if (!countryKey || !drop.uniqueId) return;
    navigate(
      `/smswiser/${encodeURIComponent(countryKey)}?src=tool&drop=${encodeURIComponent(drop.uniqueId)}`,
    );
  };

  return (
    <div className="flex flex-col gap-1.5 text-left">
      <StatsTooltipSection label="Title" valueClassName="font-medium">
        {title}
      </StatsTooltipSection>
      <StatsTooltipSection label="Campaign">{orDash(drop.campaign)}</StatsTooltipSection>
      <StatsTooltipSection label="Gateway" valueClassName="font-mono text-tooltip-gw">
        {gw}
      </StatsTooltipSection>
      <StatsTooltipSection label="Sender ID" valueClassName="font-mono text-tooltip-sid">
        {sid}
      </StatsTooltipSection>
      <StatsTooltipSection label="Leads list">{leads}</StatsTooltipSection>
      {drop.uniqueId ? (
        <StatsTooltipSection label="Drop ID" valueClassName="font-mono">
          {drop.uniqueId}
        </StatsTooltipSection>
      ) : null}
      <StatsTooltipSection label="Tag">
        {drop.staticTag?.name ?? DEFAULT_TAG_NAME}
      </StatsTooltipSection>
      {dropSequence != null ? (
        <div className="border-t border-white/20 pt-1.5 text-[10px] opacity-80">
          Send order #{dropSequence}
        </div>
      ) : null}
      <div className="mt-1.5 flex flex-row flex-wrap gap-1">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleCopyConfig();
          }}
          className="flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium hover:bg-white/15"
          aria-label="Copy campaign, gateway, senders for Apply"
        >
          <Copy className="h-3.5 w-3.5" />
          Copy config
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleCopyConfigWithTexts();
          }}
          className="flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium hover:bg-white/15"
          aria-label="Copy campaign, gateway, senders and texts for Apply"
        >
          <Copy className="h-3.5 w-3.5" />
          Copy config + texts
        </button>
        {canOpenInTool ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleOpenInTool();
            }}
            className="flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium hover:bg-white/15"
            aria-label="Open this drop in the Tool (SMSWiser) stats view"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open in Tool
          </button>
        ) : null}
      </div>
    </div>
  );
}
